import React, { useState, useEffect, useRef } from 'react'
import { Form, Button, Alert, Container, Card, Image, Row, Col } from 'react-bootstrap'
import api from '../../services/api'

const DEFAULT_COMPANY_NAME = 'БутМирСтрой'
const DEFAULT_PHONE = '+7911-130-01-13'

const Settings = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [companyName, setCompanyName] = useState('')
  const [phone, setPhone] = useState('')
  const [logoUrl, setLogoUrl] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await api.get('/settings.json')
      setCompanyName(response.data.company_name || '')
      setPhone(response.data.phone || '')
      setLogoUrl(response.data.logo_url)
    } catch (err) {
      setError('Ошибка загрузки настроек')
      console.error('Error loading settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveLogo = async () => {
    if (!window.confirm('Удалить кастомный логотип? Будет использоваться логотип по умолчанию.')) return
    try {
      setLoading(true)
      setError(null)
      const response = await api.patch('/settings.json', { setting: { remove_logo: 'true' } })
      setLogoUrl(response.data.logo_url)
      setLogoFile(null)
      setLogoPreview(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      setError('Ошибка удаления логотипа')
      console.error('Error removing logo:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)

      const formData = new FormData()
      formData.append('setting[company_name]', companyName)
      formData.append('setting[phone]', phone)
      if (logoFile) {
        formData.append('setting[logo]', logoFile)
      }

      const response = await api.patch('/settings.json', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      setCompanyName(response.data.company_name || '')
      setPhone(response.data.phone || '')
      setLogoUrl(response.data.logo_url)
      setLogoFile(null)
      setLogoPreview(null)
      if (fileInputRef.current) fileInputRef.current.value = ''

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      const serverErrors = err.response?.data?.errors
      if (serverErrors) {
        const messages = Object.entries(serverErrors)
          .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
          .join('; ')
        setError(messages)
      } else {
        setError('Ошибка сохранения настроек')
      }
      console.error('Error saving settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const displayLogoSrc = logoPreview || logoUrl || '/logo.png'

  if (loading && !companyName && !logoUrl) {
    return <div className="py-4">Загрузка...</div>
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Header>
          <h2 className="mb-0">Настройки программы</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success">Настройки успешно сохранены</Alert>}

          <Form onSubmit={handleSubmit}>
            <h5 className="mb-3 text-muted fw-semibold text-uppercase small">Данные компании в документах</h5>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Название компании</Form.Label>
                  <Form.Control
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={DEFAULT_COMPANY_NAME}
                  />
                  <Form.Text className="text-muted">
                    Если не указано, используется «{DEFAULT_COMPANY_NAME}»
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Телефон</Form.Label>
                  <Form.Control
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={DEFAULT_PHONE}
                  />
                  <Form.Text className="text-muted">
                    Если не указано, используется «{DEFAULT_PHONE}»
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Логотип</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div className="p-2 border rounded bg-white">
                  <Image
                    src={displayLogoSrc}
                    alt="Логотип компании"
                    style={{ maxHeight: '80px', maxWidth: '220px', objectFit: 'contain', display: 'block' }}
                  />
                </div>
                {logoUrl && !logoFile && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRemoveLogo}
                    disabled={loading}
                  >
                    Удалить
                  </Button>
                )}
              </div>
              <div>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  ref={fileInputRef}
                  style={{ maxWidth: '400px' }}
                />
                <Form.Text className="text-muted">
                  PNG, JPG или другой формат. Если не загружен — используется логотип по умолчанию.
                </Form.Text>
              </div>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default Settings
